import { ApiError, type Api, type Filter } from "../../../data/api";
import type { OwnInvoiceRow } from "@talentkontor/domain";
import {
  countryDefaults, parsePgArray, pgArrayLiteral, slugCandidate, slugify,
  type ArticleRow, type ExportFlatRow, type ExportInvoiceRow, type ExportKind, type FullExport, type LegalKind, type LifecycleStatus, type RightsState,
  type ExportManifest, type ExportTablePage, type MediaExport, type MediaExportItem,
  B2B_VERSION, type ConsentMethod, type LegalNotice, type LegalVersionRow,
  type SetupStatus, type StepRow,
} from "./model";
import { t } from "../../../lib/sprache";
import { isCreatorPlan, wirksameGrenzen } from "../../../model/tarif";

/**
 * Lader und Schreibwege der Seitengruppe. Jede Funktion bekommt den Datenanschluss als ersten Wert, damit die Tests
 * sie gegen die echte Demo-Datenbank laufen lassen (RLS, Wächter, Funktionsrechte wie in Produktion).
 * Ein Update, das keine Zeile trifft, ist ein Rechtefehler (RLS filtert still) und wird als solcher gemeldet.
 */

// ── Zeilen ─────────────────────────────────────────────────────────────────────

export interface OrgRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  trial_ends_at: string | null;
  created_at: string;
}
export interface SettingsRow {
  org_id: string;
  legal_name: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  /** 0266: Land ausdrücklich gewählt bzw. bestätigt; leer = die Einrichtung fragt danach */
  country_confirmed_at?: string | null;
  vat_id: string | null;
  tax_number: string | null;
  small_business: boolean;
  timezone: string;
  locale: string;
  currency: string;
  currencies: string[] | null;
  default_commission_bp: number | null;
  agency_fee_mode: string | null;
  agency_fee_bp: number | null;
  agency_fee_cents: number | null;
  payment_terms_days: number | null;
  invoice_route: string | null;
  signing_link_days: number | null;
  brand_color: string | null;
  logo_path: string | null;
  sender_name: string | null;
  reply_to_email: string | null;
  default_document_language: string | null;
  supported_languages: string[] | null;
  email_footer: string | null;
  mascot_enabled: boolean | null;
  product_name_override: string | null;
  revision_fee_cents: number | null;
  brand_payment_terms_days: number | null;
  talent_payment_terms_days: number | null;
  deal_models_enabled: string[] | null;
  due_soon_days: number | null;
  payment_check_cooldown_days: number | null;
  still_overdue_recheck_days: number | null;
  talent_answer_days: number | null;
  min_line_description_chars: number | null;
  invoice_date_workdays_only: boolean | null;
  budget_deviation_percent: number | null;
  expense_keywords: string[] | null;
  invoice_number_scheme: { monthCheck?: string; template?: string } | null;
  invoice_number_prefix: string | null;
  small_amount_threshold_cents: number | null;
  holiday_region: string | null;
  talent_profile_requirements: string[] | null;
  whatsapp_mode: string;
  invitation_days: number | null;
  talent_payout_mode: string | null;
  requested_urgent_days: number | null;
  stale_draft_days: number | null;
  flat_tax_enabled: boolean | null;
  /** Schweizer QR-Zahlteil (0069) */
  qr_bill_enabled?: boolean;
  qr_bill_reference?: "scor" | "non";
  qr_reference_prefix?: string | null;
  /** Kurs der Umsatzsteuer in Euro (0298, K-396, nur DE): leer = BMF-Monatskurs (Ländervorgabe), ecb_day = EZB-Tageskurs mit Gestattung */
  vat_eur_rate_mode?: "bmf_month" | "ecb_day" | null;
  updated_at: string;
}
export type SettingsPatch = Partial<Omit<SettingsRow, "org_id" | "updated_at">>;

export interface LegalRow {
  org_id: string;
  legal_form: string | null;
  representative_name: string | null;
  representative_position: string | null;
  register_type: string | null;
  register_number: string | null;
  register_court: string | null;
  phone: string | null;
  website: string | null;
  imprint_extra: string | null;
  privacy_contact_email: string | null;
  governing_law: string | null;
  jurisdiction: string | null;
  /** 0071: alle Vertretungsberechtigten (die erste zugleich representative_name), Gesamtvertretung */
  representatives?: { name: string; position?: string | null }[];
  joint_representation?: boolean;
  updated_at: string;
}
export type LegalPatch = Partial<Omit<LegalRow, "org_id" | "updated_at">>;

export interface BankRow {
  id: string;
  org_id: string;
  holder: string;
  iban: string;
  bic: string | null;
  bank_name: string | null;
  currency: string;
  qr_iban: string | null;
  is_default: boolean;
  created_at: string;
}
export type BankInput = Omit<BankRow, "id" | "org_id" | "created_at">;

export interface PolicyRow {
  org_id: string;
  interest_enabled: boolean;
  surcharge_bp: number;
  fee_mode: string | null;
  flat_fee_cents: number | null;
  fee_currency: string | null;
  /** Mahnung im Auftrag des Creators mit Gebühr, Pauschale und Zinsen (0161, Vorgabe an) */
  creator_claims_costs?: boolean;
  /** CH-2 (0271): Zinssatz nach Land oder vertraglich vereinbart, Zinsbeginn */
  interest_rate_mode?: "legal" | "contract";
  contract_rate_bp?: number | null;
  contract_basis?: string | null;
  interest_start?: "due" | "reminder" | null;
  /** 0570 Mahn-Autopilot: automatisch senden (Standard aus), nur erste Stufe oder alle, auch Creator-Rechnungen an Marken */
  auto_send?: boolean;
  auto_send_scope?: "first" | "all";
  auto_send_creator?: boolean;
  updated_at: string;
}
export type PolicyPatch = Partial<Pick<PolicyRow, "interest_enabled" | "surcharge_bp" | "fee_mode" | "flat_fee_cents" | "fee_currency" | "creator_claims_costs" | "interest_rate_mode" | "contract_rate_bp" | "contract_basis" | "interest_start" | "auto_send" | "auto_send_scope" | "auto_send_creator">>;
export interface StepDbRow extends StepRow {
  org_id: string;
}
export interface BaseRateRow {
  org_id: string;
  valid_from: string;
  rate_bp: number;
}
export interface PlatformTextRow {
  key: string;
  language: string;
  subject: string | null;
  body: string;
}
export interface OrgTextRow extends PlatformTextRow {
  org_id: string;
  updated_at: string;
}
export interface TemplateRow {
  id: string;
  name: string;
  language: string;
  status: string;
  version: number;
  kind_id: string | null;
  description: string | null;
  updated_at: string;
}
export interface KindRow {
  id: string;
  key: string;
  name_de: string;
  name_en: string;
}
export interface ArticleDbRow extends ArticleRow {
  id: string;
  org_id: string;
  updated_at: string;
}
export interface StatusLabelRow {
  org_id: string;
  status: string;
  label_de: string | null;
  label_en: string | null;
  hidden: boolean;
}
export interface MemberRow extends RightsState {
  /** Inhaber der Agentur (0057) */
  is_owner?: boolean;
  /** Zugang über den Verbund (0174): Rolle und Zugang legt der Betrieb fest, im Team nicht änderbar (0514) */
  via_group?: boolean;
  id: string;
  org_id: string;
  user_id: string;
  active: boolean;
  display_name: string | null;
  job_title: string | null;
  created_at: string;
  updated_at: string;
  /** Mailadresse (0220, nur für Admins geladen): steht statt „Ohne Namen“, wenn der Anzeigename fehlt */
  email?: string | null;
}
export interface AssignmentRow {
  membership_id: string;
  talent_id: string;
  org_id: string;
}
export interface InvitationRow {
  id: string;
  org_id: string;
  kind: string;
  email: string;
  role: string | null;
  talent_id: string | null;
  invited_by: string | null;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}
export interface TalentMiniRow {
  id: string;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  email: string | null;
  status: string;
}
export interface PresenceRow {
  talent_id: string;
  org_id: string;
  last_seen_at: string | null;
  last_view: string | null;
  last_login_at: string | null;
}
export interface GrantRow {
  id: string;
  org_id: string;
  platform_admin_id: string;
  reason: string;
  starts_at: string;
  expires_at: string;
  revoked_at: string | null;
}
export interface AuditRow {
  id: number;
  actor: string | null;
  actor_kind: string;
  table_name: string;
  row_id: string | null;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  at: string;
}
export interface SubscriptionRow {
  org_id: string;
  plan_key: string;
  status: string;
  billing_interval: string;
  current_period_end: string | null;
  updated_at: string;
  /** 0157: gebuchte Zusatzpakete (zählen auf die Grenzen), vorgemerkter Wechsel, Kündigung im Kundenportal, gescheiterte Zahlung */
  extra_seats?: number;
  extra_talents?: number;
  pending_plan_key?: string | null;
  pending_interval?: "month" | "year" | null;
  pending_change_at?: string | null;
  cancel_at_period_end?: boolean;
  payment_failed_at?: string | null;
}
export interface PlanRow {
  key: string;
  name: string;
  price_month_cents: number;
  price_year_cents: number;
  active: boolean;
  sort_order: number;
  /** Stripe Payment Links (0057); leer: Anfrage an den Betrieb */
  stripe_link_month?: string | null;
  stripe_link_year?: string | null;
  /** Stripe-Preise (0022); gesetzt: Selbstbedienung über die Serverfunktion billing-change (0157) */
  stripe_price_month?: string | null;
  stripe_price_year?: string | null;
}
/** Zusatzpaket (0157): je Stück ein Team-Platz bzw. ein Creator mehr; ohne Stripe-Preis nicht buchbar */
export interface AddonRow {
  key: "extra_seat" | "extra_talent";
  entitlement_key: "max_seats" | "max_talents";
  name: string;
  name_en: string;
  price_month_cents: number;
  price_year_cents: number;
  stripe_price_month: string | null;
  stripe_price_year: string | null;
  max_quantity: number;
  active: boolean;
  sort_order: number;
}
export interface EntitlementRow {
  plan_key: string;
  key: string;
  value: number;
}
export interface PrefsRow {
  user_id: string;
  language: string | null;
  initials: string | null;
  desktop_notifications: boolean;
  updated_at: string;
}

// ── Hilfen ─────────────────────────────────────────────────────────────────────

const byOrg = (orgId: string): Filter[] => [["org_id", "eq", orgId]];

/** RLS filtert Änderungen ohne Recht still weg; ohne getroffene Zeile ist das ein Rechtefehler, kein Erfolg. */
function need<T>(rows: T[], what: string): T[] {
  if (!rows.length) {
    throw new ApiError(t(`${what} wurde nicht gespeichert: keine Berechtigung oder der Eintrag existiert nicht mehr.`, `${what} was not saved: no permission or the entry no longer exists.`), { code: "42501" });
  }
  return rows;
}

/** Löschen meldet ohne Recht keinen Fehler; deshalb nachsehen, ob der Eintrag noch da ist. */
async function removeChecked(api: Api, table: string, filters: Filter[], what: string): Promise<void> {
  await api.remove(table, filters);
  const still = await api.select(table, { filters, limit: 1 });
  if (still.length) throw new ApiError(t(`${what} wurde nicht entfernt: keine Berechtigung.`, `${what} was not removed: no permission.`), { code: "42501" });
}

function normalizeSettings(row: SettingsRow): SettingsRow {
  const models = (row as { deal_models_enabled: unknown }).deal_models_enabled;
  return { ...row, deal_models_enabled: models === null || models === undefined ? null : parsePgArray(models) };
}

// ── Lesen ──────────────────────────────────────────────────────────────────────

export async function loadOrg(api: Api, orgId: string): Promise<OrgRow> {
  const rows = await api.select<OrgRow>("organizations", { columns: "id, name, slug, status, trial_ends_at, created_at", filters: [["id", "eq", orgId]] });
  const org = rows[0];
  if (!org) throw new ApiError(t("Diese Agentur ist für dein Konto nicht sichtbar.", "This agency is not visible to your account."), { code: "42501" });
  return org;
}

export async function loadSettings(api: Api, orgId: string): Promise<SettingsRow> {
  const rows = await api.select<SettingsRow>("org_settings", { filters: byOrg(orgId) });
  const row = rows[0];
  if (!row) throw new ApiError(t("Die Einstellungen dieser Agentur sind für dein Konto nicht lesbar.", "The settings of this agency are not readable for your account."), { code: "42501" });
  return normalizeSettings(row);
}

export async function loadDefaults(api: Api): Promise<Record<string, unknown>> {
  const rows = await api.select<{ key: string; value: unknown }>("platform_defaults", { columns: "key, value" });
  return Object.fromEntries(rows.map((r) => [r.key, r.value]));
}

export async function loadSetupStatus(api: Api, orgId: string): Promise<SetupStatus> {
  const r = await api.read<SetupStatus | string>("org_setup_status", { p_org: orgId });
  const s = (typeof r === "string" ? JSON.parse(r) : r) as SetupStatus;
  return { complete: !!s.complete, missing: Array.isArray(s.missing) ? s.missing : [] };
}

export interface FirmaData {
  org: OrgRow;
  settings: SettingsRow;
  legal: LegalRow | null;
  banks: BankRow[];
}
export async function loadFirma(api: Api, orgId: string): Promise<FirmaData> {
  const [org, settings, legal, banks] = await Promise.all([
    loadOrg(api, orgId),
    loadSettings(api, orgId),
    api.select<LegalRow>("org_legal", { filters: byOrg(orgId) }),
    api.select<BankRow>("org_bank_accounts", { filters: byOrg(orgId), order: [["currency", "asc"], ["created_at", "asc"]] }),
  ]);
  return { org, settings, legal: legal[0] ?? null, banks };
}

export interface SettingsData {
  settings: SettingsRow;
  defaults: Record<string, unknown>;
}
export async function loadSettingsWithDefaults(api: Api, orgId: string): Promise<SettingsData> {
  const [settings, defaults] = await Promise.all([loadSettings(api, orgId), loadDefaults(api)]);
  return { settings, defaults };
}

export interface MahnleiterData extends SettingsData {
  policy: PolicyRow | null;
  steps: StepDbRow[];
  rates: BaseRateRow[];
  /** Mahnstufen, die an Rechnungen gesetzt sind (dürfen nicht aus der Leiter verschwinden) */
  inUse: Record<string, number>;
}
export async function loadMahnleiter(api: Api, orgId: string): Promise<MahnleiterData> {
  const [base, policy, steps, rates, invoices] = await Promise.all([
    loadSettingsWithDefaults(api, orgId),
    api.select<PolicyRow>("dunning_policies", { columns: "org_id, interest_enabled, surcharge_bp, fee_mode, flat_fee_cents, fee_currency, creator_claims_costs, interest_rate_mode, contract_rate_bp, contract_basis, interest_start, auto_send, auto_send_scope, auto_send_creator, updated_at", filters: byOrg(orgId) }),
    api.select<StepDbRow>("dunning_steps", { filters: byOrg(orgId), order: [["step", "asc"]] }),
    api.select<BaseRateRow>("dunning_base_rates", { filters: byOrg(orgId), order: [["valid_from", "desc"]] }),
    api.select<{ dunning_stage: string }>("invoices", { columns: "dunning_stage", filters: [["org_id", "eq", orgId], ["dunning_stage", "neq", "none"]] }),
  ]);
  const inUse: Record<string, number> = {};
  for (const i of invoices) inUse[i.dunning_stage] = (inUse[i.dunning_stage] ?? 0) + 1;
  return { ...base, policy: policy[0] ?? null, steps, rates, inUse };
}

export interface VorlagenData {
  settings: SettingsRow;
  defaults: Record<string, unknown>;
  platformTexts: PlatformTextRow[];
  orgTexts: OrgTextRow[];
  templates: TemplateRow[];
  kinds: KindRow[];
  articles: ArticleDbRow[];
  labels: StatusLabelRow[];
  /** Mahnstufen der Leiter (eigene Stufen brauchen ggf. einen eigenen Text) */
  steps: { key: string; is_reminder: boolean }[];
}
export async function loadVorlagen(api: Api, orgId: string): Promise<VorlagenData> {
  const [base, platformTexts, orgTexts, templates, kinds, articles, labels, steps] = await Promise.all([
    loadSettingsWithDefaults(api, orgId),
    api.select<PlatformTextRow>("platform_texts", { order: [["key", "asc"], ["language", "asc"]] }),
    api.select<OrgTextRow>("org_texts", { filters: byOrg(orgId) }),
    api.select<TemplateRow>("contract_templates", { columns: "id, name, language, status, version, kind_id, description, updated_at", filters: byOrg(orgId), order: [["name", "asc"]] }),
    api.select<KindRow>("contract_kinds", { columns: "id, key, name_de, name_en", filters: byOrg(orgId), order: [["sort_order", "asc"]] }),
    loadArticles(api, orgId),
    api.select<StatusLabelRow>("org_status_labels", { filters: byOrg(orgId) }),
    api.select<{ key: string; is_reminder: boolean }>("dunning_steps", { columns: "key, is_reminder", filters: byOrg(orgId), order: [["step", "asc"]] }),
  ]);
  return { ...base, platformTexts, orgTexts, templates, kinds, articles, labels, steps };
}

export async function loadArticles(api: Api, orgId: string): Promise<ArticleDbRow[]> {
  return api.select<ArticleDbRow>("invoice_articles", { filters: byOrg(orgId), order: [["sort_order", "asc"], ["name", "asc"]] });
}

export interface TeamData {
  org: OrgRow;
  members: MemberRow[];
  assignments: AssignmentRow[];
  invitations: InvitationRow[];
  talents: TalentMiniRow[];
  presence: PresenceRow[];
  grants: GrantRow[];
  audit: AuditRow[];
  seats: { used: number; max: number | null };
  /** K-377: Test im vollen Umfang: Ende, gewählter Tarif und dessen Plätze („danach gilt …“) */
  test: { bis: string; planKey: string; planSeats: number | null } | null;
}
const TEAM_TABLES = ["memberships", "manager_talent_assignments", "invitations", "support_access_grants"];
export async function loadTeam(api: Api, orgId: string): Promise<TeamData> {
  const [org, ohneMail, assignments, invitations, talents, presence, grants, audit, sub, mails] = await Promise.all([
    loadOrg(api, orgId),
    api.select<MemberRow>("memberships", { filters: byOrg(orgId), order: [["created_at", "asc"]] }),
    api.select<AssignmentRow>("manager_talent_assignments", { filters: byOrg(orgId) }),
    api.select<InvitationRow>("invitations", {
      columns: "id, org_id, kind, email, role, talent_id, invited_by, expires_at, accepted_at, revoked_at, created_at",
      filters: [["org_id", "eq", orgId], ["kind", "eq", "member"]], order: [["created_at", "desc"]],
    }),
    api.select<TalentMiniRow>("talents", { columns: "id, first_name, last_name, display_name, email, status", filters: byOrg(orgId), order: [["first_name", "asc"]] }),
    api.select<PresenceRow>("talent_presence", { filters: byOrg(orgId) }),
    api.select<GrantRow>("support_access_grants", { columns: "id, org_id, platform_admin_id, reason, starts_at, expires_at, revoked_at", filters: byOrg(orgId), order: [["starts_at", "desc"]], limit: 20 }),
    api.select<AuditRow>("audit_log", { columns: "id, actor, actor_kind, table_name, row_id, action, before, after, at", filters: [["org_id", "eq", orgId], ["table_name", "in", TEAM_TABLES]], order: [["at", "desc"]], limit: 12 }),
    loadSubscriptionUsage(api, orgId),
    api.read<unknown>("org_member_emails", { p_org: orgId }).then((r) => (typeof r === "string" ? JSON.parse(r) : r) as Record<string, string>).catch(() => ({} as Record<string, string>)),
  ]);
  const members = ohneMail.map((m) => ({ ...m, email: mails?.[m.user_id] ?? null }));
  const test = sub.fullTrial && org.trial_ends_at && sub.subscription ? { bis: org.trial_ends_at, planKey: sub.subscription.plan_key, planSeats: sub.planLimits.max_seats ?? null } : null;
  return { org, members, assignments, invitations, talents, presence, grants, audit, seats: { used: members.filter((m) => m.active).length, max: sub.limits.max_seats ?? null }, test };
}

export interface AboData {
  org: OrgRow;
  settings: SettingsRow;
  subscription: SubscriptionRow | null;
  plans: PlanRow[];
  entitlements: EntitlementRow[];
  /** Wirksame Grenzen und Module (org_entitlements; im Test im vollen Umfang die des Tarifs Agentur, K-377) */
  limits: Record<string, number>;
  /** Grenzen des gewählten Tarifs mit Zusatzpaketen (gelten nach dem Test) */
  planLimits: Record<string, number>;
  /** Test im vollen Umfang läuft (0216) */
  fullTrial: boolean;
  used: { talents: number; seats: number; contractsThisMonth: number };
  /** Kontaktadresse des Betriebs für Abo-Fragen (platform_defaults.support_email); leer: nur die Anfrage im Portal */
  supportEmail: string | null;
  /** Dauer der Testphase in Tagen (platform_defaults.trial_days) */
  trialDays: number | null;
  /** Zusatzpakete (0157); leer, solange keine angeboten werden */
  addons: AddonRow[];
}
const SUB_COLUMNS = "org_id, plan_key, status, billing_interval, current_period_end, updated_at, extra_seats, extra_talents, pending_plan_key, pending_interval, pending_change_at, cancel_at_period_end, payment_failed_at";
/** Grenzen des Abos wie app.entitlement (0157): Zusatzpakete zählen auf begrenzte Plätze und Creator. */
export function limitsWithExtras(entitlements: readonly EntitlementRow[], planKey: string | null | undefined, extras: { seats?: number | null; talents?: number | null }): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of entitlements) {
    if (e.plan_key !== planKey) continue;
    const plus = e.key === "max_seats" ? extras.seats ?? 0 : e.key === "max_talents" ? extras.talents ?? 0 : 0;
    out[e.key] = e.value >= 0 ? e.value + Math.max(0, plus) : e.value;
  }
  return out;
}
/**
 * Abo, Katalog und Grenzen. K-377: `limits` sind die wirksamen Grenzen aus org_entitlements (im Test im vollen Umfang die des
 * Tarifs Agentur, 0216), `planLimits` die des gewählten Tarifs mit Zusatzpaketen (für „danach gilt …“).
 */
async function loadSubscriptionUsage(api: Api, orgId: string) {
  const [subs, entitlements] = await Promise.all([
    api.select<SubscriptionRow>("subscriptions", { columns: SUB_COLUMNS, filters: byOrg(orgId) }),
    api.select<EntitlementRow>("plan_entitlements"),
  ]);
  const subscription = subs[0] ?? null;
  const planLimits = limitsWithExtras(entitlements, subscription?.plan_key, { seats: subscription?.extra_seats, talents: subscription?.extra_talents });
  const { limits, fullTrial } = await wirksameGrenzen(api, orgId, planLimits);
  return { subscription, entitlements, limits, planLimits, fullTrial };
}
/**
 * Stand für die Conversion nach der Rückkehr aus Stripe/PayPal (Inhaber 02.11.2026): im Test mit hinterlegter Zahlungsart
 * („Test mit Karte“) oder schon bezahltes Abo; Wert = Preis des gebuchten Tarifs für den ersten Zeitraum (netto, Euro), 0 wenn unbekannt.
 */
export async function aboStand(api: Api, orgId: string): Promise<{ imTest: boolean; wertEuro: number }> {
  const subs = await api.select<{ plan_key: string | null; billing_interval: string | null; status: string | null }>("subscriptions", { columns: "plan_key, billing_interval, status", filters: byOrg(orgId) });
  const sub = subs[0];
  const imTest = !sub || sub.status === "trialing";
  if (!sub?.plan_key) return { imTest, wertEuro: 0 };
  const plans = await api.select<{ price_month_cents: number | null; price_year_cents: number | null }>("plans", { columns: "price_month_cents, price_year_cents", filters: [["key", "eq", sub.plan_key]] });
  const cent = sub.billing_interval === "year" ? plans[0]?.price_year_cents : plans[0]?.price_month_cents;
  return { imTest, wertEuro: (cent ?? 0) / 100 };
}
/** Monat eines Zeitpunkts in der Zeitzone der Agentur („2026-09“), so wie app.check_contract_limit zählt. */
export function monthKeyIn(iso: string | Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit" }).format(typeof iso === "string" ? new Date(iso) : iso);
}
export async function loadAbo(api: Api, orgId: string, timeZone: string): Promise<AboData> {
  const [org, settings, plans, sub, talents, members, contracts, defaults, addons] = await Promise.all([
    loadOrg(api, orgId),
    loadSettings(api, orgId),
    api.select<PlanRow>("plans", { columns: "key, name, price_month_cents, price_year_cents, active, sort_order, stripe_link_month, stripe_link_year, stripe_price_month, stripe_price_year", order: [["sort_order", "asc"]] }),
    loadSubscriptionUsage(api, orgId),
    // K-436: Beispieldaten (0325) zählen nicht in den Tarifgrenzen
    api.select<{ id: string }>("talents", { columns: "id", filters: [["org_id", "eq", orgId], ["status", "neq", "archived"], ["is_sample", "eq", false]] }),
    api.select<{ id: string }>("memberships", { columns: "id", filters: [["org_id", "eq", orgId], ["active", "is", true]] }),
    // Versendete Verträge der letzten fünf Wochen; gezählt wird der laufende Monat in der Zeitzone der Agentur
    api.select<{ sent_at: string }>("contracts", { columns: "sent_at", filters: [["org_id", "eq", orgId], ["sent_at", "gte", new Date(Date.now() - 35 * 86400000).toISOString()]] }),
    api.select<{ key: string; value: unknown }>("platform_defaults", { columns: "key, value", filters: [["key", "in", ["support_email", "trial_days"]]] }),
    api.select<AddonRow>("plan_addons", { order: [["sort_order", "asc"]] }).catch(() => [] as AddonRow[]),
  ]);
  const support = String(defaults.find((x) => x.key === "support_email")?.value ?? "").trim();
  const trialDays = Number(defaults.find((x) => x.key === "trial_days")?.value ?? NaN);
  // Creator-Konto (0178): nur der Tarif Creator, keine Zusatzpakete; Agenturen sehen den Creator-Tarif nicht
  const creator = isCreatorPlan(sub.subscription?.plan_key);
  return {
    supportEmail: support || null, trialDays: Number.isFinite(trialDays) ? trialDays : null, addons: creator ? [] : addons.filter((a) => a.active),
    org, settings, plans: plans.filter((p) => isCreatorPlan(p.key) === creator), subscription: sub.subscription, entitlements: sub.entitlements, limits: sub.limits,
    planLimits: sub.planLimits, fullTrial: sub.fullTrial,
    used: { talents: talents.length, seats: members.length, contractsThisMonth: contracts.filter((c) => monthKeyIn(c.sent_at, timeZone) === monthKeyIn(new Date(), timeZone)).length },
  };
}

export interface ProfilData {
  membership: MemberRow | null;
  prefs: PrefsRow | null;
  assigned: TalentMiniRow[];
}
export async function loadProfil(api: Api, userId: string, orgId: string): Promise<ProfilData> {
  const [members, prefs] = await Promise.all([
    api.select<MemberRow>("memberships", { filters: [["org_id", "eq", orgId], ["user_id", "eq", userId]] }),
    api.select<PrefsRow>("user_preferences", { columns: "user_id, language, initials, desktop_notifications, updated_at", filters: [["user_id", "eq", userId]] }),
  ]);
  const membership = members[0] ?? null;
  let assigned: TalentMiniRow[] = [];
  if (membership && membership.role === "manager" && !membership.sees_all_talents) {
    const a = await api.select<AssignmentRow>("manager_talent_assignments", { filters: [["membership_id", "eq", membership.id]] });
    assigned = a.length
      ? await api.select<TalentMiniRow>("talents", { columns: "id, first_name, last_name, display_name, email, status", filters: [["id", "in", a.map((x) => x.talent_id)]], order: [["first_name", "asc"]] })
      : [];
  }
  return { membership, prefs: prefs[0] ?? null, assigned };
}

// ── Schreiben: Firma, Recht, Bank ───────────────────────────────────────────────

export async function updateSettings(api: Api, orgId: string, patch: SettingsPatch): Promise<void> {
  if (!Object.keys(patch).length) return;
  const clean: Record<string, unknown> = { ...patch };
  // Enum-Listen als Postgres-Listentext: der Demo-Anschluss kennt den Typ deal_model[] nicht (siehe Bericht)
  if (Array.isArray(patch.deal_models_enabled)) clean.deal_models_enabled = pgArrayLiteral(patch.deal_models_enabled);
  need(await api.update("org_settings", byOrg(orgId), clean), t("Die Einstellung", "The setting"));
  // B-1 (Inhaber 27.10.2026): geänderte USt-IdNr. an den Stripe-Kunden, damit die nächste Rechnung per Reverse Charge läuft.
  // Ohne Abo bzw. ohne Stripe passiert nichts; ein Fehler steht im Protokoll der Agentur und hält das Speichern nicht auf
  if ("vat_id" in patch && api.billing) void api.billing("billing-change", { org: orgId, action: "tax_id_sync" }).catch(() => undefined);
}

export async function upsertLegal(api: Api, orgId: string, patch: LegalPatch): Promise<void> {
  if (!Object.keys(patch).length) return;
  const rows = await api.update("org_legal", byOrg(orgId), patch);
  // Ältere Agenturen ohne Rechtsdatensatz: anlegen (Manager scheitern hier an RLS)
  if (!rows.length) await api.insert("org_legal", { org_id: orgId, ...patch });
}

export async function renameOrg(api: Api, orgId: string, name: string): Promise<void> {
  need(await api.update("organizations", [["id", "eq", orgId]], { name }), t("Der Agenturname", "The agency name"));
}

export async function saveFirma(api: Api, orgId: string, p: { orgName?: string; settings: SettingsPatch; legal: LegalPatch }): Promise<void> {
  if (p.orgName !== undefined) await renameOrg(api, orgId, p.orgName);
  await updateSettings(api, orgId, p.settings);
  await upsertLegal(api, orgId, p.legal);
}

export async function addBankAccount(api: Api, orgId: string, input: BankInput): Promise<BankRow> {
  if (input.is_default) {
    await api.update("org_bank_accounts", [["org_id", "eq", orgId], ["currency", "eq", input.currency], ["is_default", "is", true]], { is_default: false });
  }
  const rows = await api.insert<BankRow>("org_bank_accounts", { org_id: orgId, ...input });
  return need(rows, t("Das Bankkonto", "The bank account"))[0]!;
}
/** QR-IBAN eines bestehenden Kontos setzen oder entfernen (0069; die Datenbank prüft sie noch einmal) */
export async function setBankQrIban(api: Api, id: string, qrIban: string | null): Promise<void> {
  need(await api.update("org_bank_accounts", [["id", "eq", id]], { qr_iban: qrIban }), t("Die QR-IBAN", "The QR-IBAN"));
}
export async function setDefaultBankAccount(api: Api, orgId: string, account: Pick<BankRow, "id" | "currency">): Promise<void> {
  await api.update("org_bank_accounts", [["org_id", "eq", orgId], ["currency", "eq", account.currency], ["is_default", "is", true]], { is_default: false });
  need(await api.update("org_bank_accounts", [["id", "eq", account.id]], { is_default: true }), t("Das Standardkonto", "The default account"));
}
export async function deleteBankAccount(api: Api, id: string): Promise<void> {
  await removeChecked(api, "org_bank_accounts", [["id", "eq", id]], t("Das Bankkonto", "The bank account"));
}
/** Offene eigene Rechnungen einer Währung (freigegeben oder versendet, noch nicht bezahlt): nennen dieses Konto. */
export async function countOpenAgencyInvoices(api: Api, orgId: string, currency: string): Promise<number> {
  const rows = await api.select<{ id: string }>("invoices", {
    columns: "id", filters: [["org_id", "eq", orgId], ["kind", "eq", "agency_to_brand"], ["currency", "eq", currency], ["status", "in", ["approved", "sent"]]],
  });
  return rows.length;
}

// ── Schreiben: Mahnleiter ──────────────────────────────────────────────────────

export async function savePolicy(api: Api, orgId: string, patch: PolicyPatch): Promise<void> {
  if (!Object.keys(patch).length) return;
  const rows = await api.update("dunning_policies", byOrg(orgId), patch);
  if (!rows.length) await api.insert("dunning_policies", { org_id: orgId, ...patch });
}

/**
 * Leiter speichern: vorhandene Stufen ändern, neue anlegen, überzählige entfernen. Eine Stufe, die an Rechnungen
 * gesetzt ist, darf nicht verschwinden (sonst kennt die Fachlogik die Stufe dieser Rechnungen nicht mehr).
 */
export async function saveLadder(api: Api, orgId: string, rows: StepRow[], before: StepDbRow[], inUse: Record<string, number>): Promise<void> {
  const keep = new Set(rows.map((r) => r.key));
  const lost = Object.keys(inUse).filter((k) => !keep.has(k) && inUse[k]! > 0);
  if (lost.length) {
    throw new ApiError(t(`Die Stufe „${lost.join("“, „")}“ ist an Rechnungen gesetzt und bleibt deshalb in der Leiter.`, `The level “${lost.join("“, „")}” is set on invoices and therefore stays in the ladder.`), { code: "P0001" });
  }
  const existing = new Set(before.map((s) => s.step));
  for (const r of rows) {
    const values = { key: r.key, days: r.days, day_type: r.day_type, fee_cents: r.fee_cents, interest: r.interest, is_reminder: r.is_reminder };
    if (existing.has(r.step)) need(await api.update("dunning_steps", [["org_id", "eq", orgId], ["step", "eq", r.step]], values), t(`Stufe ${r.step}`, `Level ${r.step}`));
    else await api.insert("dunning_steps", { org_id: orgId, step: r.step, ...values });
  }
  const extra = before.filter((s) => s.step > rows.length);
  if (extra.length) await removeChecked(api, "dunning_steps", [["org_id", "eq", orgId], ["step", "gt", rows.length]], t("Die überzählige Stufe", "The surplus level"));
}

export async function addBaseRate(api: Api, orgId: string, validFrom: string, rateBp: number): Promise<void> {
  const rows = await api.update("dunning_base_rates", [["org_id", "eq", orgId], ["valid_from", "eq", validFrom]], { rate_bp: rateBp });
  if (!rows.length) await api.insert("dunning_base_rates", { org_id: orgId, valid_from: validFrom, rate_bp: rateBp });
}
export async function deleteBaseRate(api: Api, orgId: string, validFrom: string): Promise<void> {
  await removeChecked(api, "dunning_base_rates", [["org_id", "eq", orgId], ["valid_from", "eq", validFrom]], t("Der Basiszins", "The base rate"));
}

// ── Schreiben: Texte, Artikel, Begriffe ────────────────────────────────────────

export async function saveOrgText(api: Api, orgId: string, key: string, language: string, subject: string | null, body: string): Promise<void> {
  const f: Filter[] = [["org_id", "eq", orgId], ["key", "eq", key], ["language", "eq", language]];
  const rows = await api.update("org_texts", f, { subject, body });
  if (!rows.length) await api.insert("org_texts", { org_id: orgId, key, language, subject, body });
}
export async function resetOrgText(api: Api, orgId: string, key: string, language: string): Promise<void> {
  await removeChecked(api, "org_texts", [["org_id", "eq", orgId], ["key", "eq", key], ["language", "eq", language]], t("Die eigene Fassung", "The own version"));
}

export async function saveArticle(api: Api, orgId: string, id: string | null, row: ArticleRow): Promise<ArticleDbRow> {
  if (id) return need(await api.update<ArticleDbRow>("invoice_articles", [["id", "eq", id]], { ...row }), t("Der Artikel", "The item"))[0]!;
  return need(await api.insert<ArticleDbRow>("invoice_articles", { org_id: orgId, ...row }), t("Der Artikel", "The item"))[0]!;
}
export async function deleteArticle(api: Api, id: string): Promise<void> {
  await removeChecked(api, "invoice_articles", [["id", "eq", id]], t("Der Artikel", "The item"));
}

export async function saveStatusLabel(api: Api, orgId: string, status: string, v: { label_de: string | null; label_en: string | null; hidden: boolean }): Promise<void> {
  const f: Filter[] = [["org_id", "eq", orgId], ["status", "eq", status]];
  if (!v.label_de && !v.label_en && !v.hidden) {
    await removeChecked(api, "org_status_labels", f, t("Die Bezeichnung", "The label"));
    return;
  }
  const rows = await api.update("org_status_labels", f, v);
  if (!rows.length) await api.insert("org_status_labels", { org_id: orgId, status, ...v });
}

// ── Schreiben: Team ─────────────────────────────────────────────────────────────

export async function saveMember(api: Api, membershipId: string, patch: Partial<RightsState> & { active?: boolean; display_name?: string | null; job_title?: string | null }): Promise<void> {
  if (!Object.keys(patch).length) return;
  need(await api.update("memberships", [["id", "eq", membershipId]], patch), t("Die Änderung am Teammitglied", "The change to the team member"));
}

export async function saveAssignments(api: Api, orgId: string, membershipId: string, diff: { add: string[]; remove: string[] }): Promise<void> {
  if (diff.add.length) await api.insert("manager_talent_assignments", diff.add.map((t) => ({ membership_id: membershipId, talent_id: t, org_id: orgId })));
  if (diff.remove.length) {
    await removeChecked(api, "manager_talent_assignments", [["membership_id", "eq", membershipId], ["talent_id", "in", diff.remove]], t("Die Zuweisung", "The assignment"));
  }
}

/**
 * Manager einladen: Name und Rechte gleich in der Einladung (0042: invitation_create mit Anzeigename, Standardrechte ohne
 * Finanzaktionen; invitation_set_rights für die gewählten Rechte). Admins haben immer alle Rechte.
 */
export async function inviteMember(api: Api, orgId: string, email: string, role: "manager" | "chef_manager",
  opts: { name?: string | null; rights?: Partial<Omit<RightsState, "role">> } = {}) {
  const r = await api.rpc<{ invitation_id: string; expires_at: string; token: string }>("invitation_create", {
    p_kind: "member", p_email: email, p_org: orgId, p_role: role, p_display_name: opts.name?.trim() || null,
  });
  if (role === "manager" && opts.rights) await api.rpc("invitation_set_rights", { p_invitation: r.invitation_id, p_rights: opts.rights });
  return r;
}
/** Übergabe der Agentur in einem Schritt (org_handover, 0057). */
export async function handover(api: Api, orgId: string, to: string, options: {
  self: "keep" | "manager" | "leave"; transfer_owner: boolean; representative_name: string | null; representative_position: string | null; reply_to_email: string | null;
}): Promise<void> {
  await api.rpc("org_handover", { p_org: orgId, p_to: to, p_options: options });
}
export async function resendInvitation(api: Api, id: string) {
  return api.rpc<{ invitation_id: string; expires_at: string }>("invitation_resend", { p_invitation: id });
}
export async function revokeInvitation(api: Api, id: string): Promise<void> {
  await api.rpc("invitation_revoke", { p_invitation: id });
}
export async function endSupportGrant(api: Api, id: string): Promise<void> {
  need(await api.update("support_access_grants", [["id", "eq", id], ["revoked_at", "is", null]], { revoked_at: new Date().toISOString() }), t("Das Ende des Support-Zugangs", "Ending the support access"));
}

export async function saveWhatsappMode(api: Api, orgId: string, mode: "off" | "dry_run" | "live"): Promise<void> {
  need(await api.update("org_settings", byOrg(orgId), { whatsapp_mode: mode }), t("Der WhatsApp-Modus", "The WhatsApp mode"));
}

// ── Schreiben: Profil ──────────────────────────────────────────────────────────

/** Eigener Anzeigename und eigene Funktion (für jede Person; 0031 membership_update_self). */
export async function saveMemberSelf(api: Api, orgId: string, displayName: string, jobTitle: string | null): Promise<void> {
  await api.rpc("membership_update_self", { p_org: orgId, p_display_name: displayName, p_job_title: jobTitle });
}

/** Kontolöschung beantragen: der Zugang endet sofort, das Anmeldekonto löscht der Plattform-Betrieb (0042). */
export async function requestAccountDeletion(api: Api, reason: string | null): Promise<void> {
  await api.rpc("account_delete_request", { p_reason: reason });
}

/** Fehlende Zustimmungen der angemeldeten Person (AGB, Datenschutz, AVV) mit der aktuellen Fassung. */
export async function loadMissingConsents(api: Api): Promise<{ kind: LegalKind; version: string }[]> {
  const r = await api.read<{ kind: LegalKind; version: string }[] | string | null>("consent_missing");
  // Inhaber 28.10.2026: Eine neue Fassung der Datenschutzerklärung erscheint bestehenden Konten nicht mehr als Hinweis im Dashboard.
  // Sie ist eine Information, keine Zustimmung; bei der Registrierung wird sie weiter mit angekreuzt.
  return ((typeof r === "string" ? JSON.parse(r) : r) ?? []).filter((i: { kind: LegalKind }) => i.kind !== "privacy");
}
/** Browserkennung für den Zustimmungsnachweis (die Datenbank nimmt bevorzugt die Kopfzeile der Anfrage, 0182) */
function userAgent(): string | null {
  return typeof navigator !== "undefined" && navigator.userAgent ? navigator.userAgent.slice(0, 400) : null;
}
/** Zustimmungen speichern, mit Weg und Kunde (0182: user_consents.method, customer_org_id; jede Zustimmung auch in consent_events). */
export async function recordConsents(api: Api, items: readonly { kind: string; version: string }[], opts: { method?: ConsentMethod | null; orgId?: string | null } = {}): Promise<void> {
  for (const i of items) {
    await api.rpc("consent_record", { p_kind: i.kind, p_version: i.version, p_method: opts.method ?? null, p_org: opts.orgId ?? null, p_user_agent: userAgent() });
  }
}
/** B2B-Bestätigung speichern (Registrierung, Checkout); die Fassung prüft die Datenbank (b2b_declaration_version). */
export async function recordB2bDeclaration(api: Api, method: ConsentMethod, orgId?: string | null): Promise<void> {
  await recordConsents(api, [{ kind: "b2b_declaration", version: B2B_VERSION }], { method, orgId: orgId ?? null });
}
/** Eigene Zustimmungen der angemeldeten Person (alle Fassungen, neueste zuerst; RLS: nur eigene Zeilen) */
export interface OwnConsentRow { kind: string; version: string; accepted_at: string; method: string | null; customer_org_id: string | null }
export async function loadOwnConsents(api: Api): Promise<OwnConsentRow[]> {
  const uid = api.session()?.userId;
  if (!uid) return [];
  return api.select<OwnConsentRow>("user_consents", { columns: "kind, version, accepted_at, method, customer_org_id", filters: [["user_id", "eq", uid]], order: [["accepted_at", "desc"]] });
}
/** Fassungen der Rechtstexte (Archiv) und die heute wirksamen Fassungen (0182) */
export async function loadLegalVersions(api: Api): Promise<{ rows: LegalVersionRow[]; current: Record<string, string> }> {
  const [rows, current] = await Promise.all([
    api.select<LegalVersionRow>("legal_document_versions", { order: [["kind", "asc"], ["version", "desc"]] }),
    api.read<Record<string, string> | string | null>("legal_versions_current"),
  ]);
  return { rows, current: (typeof current === "string" ? JSON.parse(current) : current) ?? {} };
}
/** Angekündigte Änderungen der Rechtstexte für Admins (0182); für andere leer */
export async function loadLegalNotices(api: Api): Promise<LegalNotice[]> {
  const r = await api.read<LegalNotice[] | string | null>("legal_change_notices_open");
  return (typeof r === "string" ? JSON.parse(r) : r) ?? [];
}

export async function savePrefs(api: Api, userId: string, patch: { language?: string | null; initials?: string | null; desktop_notifications?: boolean }): Promise<void> {
  if (!Object.keys(patch).length) return;
  const rows = await api.update("user_preferences", [["user_id", "eq", userId]], patch);
  if (!rows.length) await api.insert("user_preferences", { user_id: userId, ...patch });
}

// ── Enterprise-Verbund (0174) ───────────────────────────────────────────────────

export interface GroupOverview {
  id: string;
  name: string;
  billing_org_id: string;
  billing_org_name: string | null;
  is_group_admin: boolean;
  plan_key: string | null;
  max_talents: number | null;
  max_seats: number | null;
  talents: number;
  seats: number;
  orgs: { id: string; name: string; status: string; is_billing: boolean; talents: number; seats: number }[] | null;
  admins: { user_id: string; email: string; role: "group_owner" | "group_admin" }[] | null;
  pending: { id: string; name: string }[] | null;
}
/** Verbund der Agentur oder null (keine Gruppe). Orte, Admins und vorgemerkte Aufnahmen nur für Gruppen-Admins. */
export async function loadGroupOverview(api: Pick<Api, "read">, orgId: string): Promise<GroupOverview | null> {
  const r = await api.read<GroupOverview | string | null>("org_group_overview", { p_org: orgId });
  if (!r) return null;
  const g = (typeof r === "string" ? JSON.parse(r) : r) as GroupOverview;
  return { ...g, talents: Number(g.talents ?? 0), seats: Number(g.seats ?? 0) };
}

// ── Lebenszyklus (0042) ─────────────────────────────────────────────────────────

export async function loadLifecycle(api: Api, orgId: string): Promise<LifecycleStatus> {
  const r = await api.read<LifecycleStatus | string>("org_lifecycle_status", { p_org: orgId });
  return (typeof r === "string" ? JSON.parse(r) : r) as LifecycleStatus;
}
/** Nur-Lesen- bzw. Sperrstand für Admin, Team und Creator (org_read_only_state; 0425: ended = Vertrag beendet, Portal gesperrt) */
export interface ReadOnlyState {
  read_only: boolean; reason?: string; documents_until?: string | null; paused_until?: string | null; ended?: boolean;
  /** 0425 (S-2): nie bezahlt (Test abgelaufen oder in der Testphase beendet) */
  trial?: boolean;
  /** 0425: Merker des Betriebs „Nur-Lesen statt Sperre“ */
  readonly_instead_of_lock?: boolean;
  /** 0436: Zustimmung zur AGB vom 23.10.2026 (bzw. neue Agentur) und daraus die Regel nach Vertragsende */
  new_terms_accepted?: boolean;
  old_terms_rule?: boolean;
  /** 0439: Anbieterwechsel läuft (bis switching_until kein Vertragsende); Eingang und Notiz nur für den Betrieb */
  switching?: boolean;
  switching_until?: string | null;
  switching_requested_at?: string | null;
  switching_note?: string | null;
}
export async function loadReadOnlyState(api: Api, orgId: string): Promise<ReadOnlyState> {
  const r = await api.read<ReadOnlyState | string>("org_read_only_state", { p_org: orgId });
  return (typeof r === "string" ? JSON.parse(r) : r) as ReadOnlyState;
}
/**
 * Kündigung vormerken (0160): Läuft das Abo über Stripe, kündigt die Serverfunktion billing-change dort zum Laufzeitende und
 * merkt die Kündigung zugleich in der App vor; sonst wie bisher nur in der App. Die Datenbank verweigert den App-Weg, solange ein
 * Stripe-Abo läuft (Hinweis billing:use_stripe), damit Abbuchung und Kündigung nie auseinanderlaufen.
 */
export async function requestCancellation(api: Api, orgId: string, reason: string | null): Promise<{ effective_at: string }> {
  if (api.billing) {
    try {
      const r = await api.billing<{ effective_at: string }>("billing-change", { org: orgId, action: "cancel", reason });
      return { effective_at: r.effective_at };
    } catch (e) {
      if (!(e instanceof ApiError && (e.hint === "billing:cancel_no_subscription" || e.hint === "billing:stripe_not_configured"))) throw e;
    }
  }
  return api.rpc<{ effective_at: string }>("org_cancel_request", { p_org: orgId, p_reason: reason });
}
export async function withdrawCancellation(api: Api, orgId: string): Promise<void> {
  if (api.billing) {
    try {
      await api.billing("billing-change", { org: orgId, action: "resume" });
      return;
    } catch (e) {
      if (!(e instanceof ApiError && (e.hint === "billing:cancel_no_subscription" || e.hint === "billing:stripe_not_configured"))) throw e;
    }
  }
  await api.rpc("org_cancel_withdraw", { p_org: orgId });
}
/**
 * Abo pausieren (0414): mit Stripe über billing-change (pause_collection mit resumes_at), sonst (vom Betrieb freigeschaltetes Abo,
 * Demo) direkt in der Datenbank. Wie beim Kündigen verweigert die Datenbank den App-Weg, solange ein Stripe-Abo läuft.
 */
export async function pauseAbo(api: Api, orgId: string, months: number, flow: string | null): Promise<{ starts_at: string; ends_at: string }> {
  if (api.billing) {
    try {
      return await api.billing<{ starts_at: string; ends_at: string }>("billing-change", { org: orgId, action: "pause", months, ...(flow ? { flow } : {}) });
    } catch (e) {
      if (!(e instanceof ApiError && (e.hint === "billing:pause_no_subscription" || e.hint === "billing:stripe_not_configured"))) throw e;
    }
  }
  return api.rpc<{ starts_at: string; ends_at: string }>("org_pause_request", { p_org: orgId, p_months: months, p_flow: flow });
}
/** Pause vorzeitig beenden bzw. vorgemerkte Pause zurücknehmen (0414) */
export async function pauseBeenden(api: Api, orgId: string): Promise<void> {
  if (api.billing) {
    try {
      await api.billing("billing-change", { org: orgId, action: "pause_end" });
      return;
    } catch (e) {
      if (!(e instanceof ApiError && (e.hint === "billing:pause_no_subscription" || e.hint === "billing:stripe_not_configured"))) throw e;
    }
  }
  await api.rpc("org_pause_end", { p_org: orgId });
}

/** Zahlen für Schritt 1 des Kündigungswegs (0415) */
export interface WegFakten {
  creators: number; deals_running: number; contracts_signed: number; invoices_open: number; invoices_open_cents: number; currency: string;
  deadlines_30d: number; read_only_days: number;
}
export async function loadWegFakten(api: Api, orgId: string): Promise<WegFakten> {
  const r = await api.read<WegFakten | string>("cancel_flow_facts", { p_org: orgId });
  const d = (typeof r === "string" ? JSON.parse(r) : r) as WegFakten;
  return { ...d, invoices_open_cents: Number(d.invoices_open_cents ?? 0) };
}
/** Ein Schritt des Kündigungswegs fürs Protokoll (0415); ein Fehler hier hält den Weg nie auf */
export async function logWeg(api: Api, orgId: string, id: string, data: Record<string, unknown>): Promise<void> {
  await api.rpc("cancel_flow_log", { p_org: orgId, p_id: id, p_data: data }).catch(() => undefined);
}
/** Support-Ticket aus dem Kündigungsweg: fehlende Funktion, Einrichtung durch uns, Gespräch (0415) */
export async function wegTicket(api: Api, orgId: string, kind: "funktion" | "einrichtung" | "gespraech", note: string | null): Promise<void> {
  await api.rpc("cancel_flow_ticket", { p_org: orgId, p_kind: kind, p_note: note?.trim() || null });
}

export async function requestEarlyPurge(api: Api, orgId: string, confirmed: boolean): Promise<void> {
  await api.rpc("org_purge_early_request", { p_org: orgId, p_confirm: confirmed });
}
export async function withdrawEarlyPurge(api: Api, orgId: string): Promise<void> {
  await api.rpc("org_purge_early_withdraw", { p_org: orgId });
}
/**
 * Eigene Rechnungen der Agentur bzw. des Creator-Kontos (an Marken und Creator) mit Rechnungsdatum im Kalenderjahr. Gezählt wird
 * mit der gemeinsamen Regel ownInvoiceRevenue (Domain, Praxistest K-254), dieselbe wie in der Übersicht des Creator-Kontos.
 */
export async function loadOwnInvoicesYear(api: Api, orgId: string, year: number): Promise<OwnInvoiceRow[]> {
  return api.select<OwnInvoiceRow>("invoices", {
    // K-315: mit dem Versandkurs, damit Rechnungen in anderen Währungen mitzählen (ownInvoiceRevenueAll)
    columns: "status, issue_date, net_cents, gross_cents, currency, credit_for, costs_for, cancelled_at, tax_mode, fx_rate_to_org",
    filters: [["org_id", "eq", orgId], ["kind", "in", ["agency_to_brand", "agency_to_talent"]], ["issue_date", "gte", `${year}-01-01`], ["issue_date", "lte", `${year}-12-31`]],
  });
}

/** Tarif wählen: Anfrage an den Plattform-Betrieb (auch in der Nur-Lesen-Phase, 0057). */
export async function requestPlan(api: Api, orgId: string, planKey: string, interval: "month" | "year", note: string | null): Promise<void> {
  await api.rpc("org_subscription_request", { p_org: orgId, p_plan: planKey, p_interval: interval, p_note: note?.trim() || null });
}
/** Testtarif wechseln (nur in der Testphase ohne Stripe-Abo, 0057). */
export async function chooseTrialPlan(api: Api, orgId: string, planKey: string): Promise<void> {
  await api.rpc("org_trial_choose_plan", { p_org: orgId, p_plan: planKey });
}
/**
 * Checkout vormerken (nur Admins, 0125): liefert ein einmaliges Kennzeichen „co_…“, 24 Stunden gültig. Nur darüber
 * ordnet der Stripe-Webhook einen Checkout sicher dieser Agentur zu; eine bloße Agentur-ID im Link könnte jeder setzen (G-SRV-1).
 */
export async function startCheckout(api: Api, orgId: string): Promise<string> {
  return api.rpc<string>("org_checkout_start", { p_org: orgId });
}
/** Zahlungslink des Tarifs mit Zuordnung (Stripe Payment Link, client_reference_id = Vormerkung aus startCheckout); ohne Link null. */
export function checkoutUrl(plan: Pick<PlanRow, "stripe_link_month" | "stripe_link_year">, interval: "month" | "year", reference: string, email: string | null): string | null {
  const base = interval === "year" ? plan.stripe_link_year : plan.stripe_link_month;
  if (!base || !/^https:\/\//.test(base)) return null;
  const url = new URL(base);
  url.searchParams.set("client_reference_id", reference);
  if (email) url.searchParams.set("prefilled_email", email);
  return url.href;
}

// ── Abo-Selbstbedienung (0157): Serverfunktionen billing-change und billing-portal ─────────────────────────────

/** Gibt es die Selbstbedienung über Stripe? Nur mit Supabase (die Demo hat keine Serverfunktionen). */
export function billingAvailable(api: Api): boolean {
  return typeof api.billing === "function";
}
/** Sitzung im Stripe-Kundenportal (Rechnungen, Zahlungsart); Rückkehr auf die Abo-Seite. Nur Admins (prüft die Datenbank). */
export async function openBillingPortal(api: Api, orgId: string, flow?: "payment_method_update"): Promise<string> {
  if (!api.billing) throw new ApiError(t("Das Kundenportal gibt es nur in der echten App, nicht in der Demo.", "The customer portal is only available in the real app, not in the demo."), { hint: "billing:demo" });
  const r = await api.billing<{ url: string }>("billing-portal", flow ? { org: orgId, flow } : { org: orgId });
  if (!r?.url || !/^https:\/\//.test(r.url)) throw new ApiError(t("Die Seite für Rechnungen und Zahlungsart ließ sich nicht öffnen. Bitte gleich noch einmal versuchen.", "The page for invoices and payment method could not be opened. Please try again shortly."));
  return r.url;
}
export type BillingChangeBody =
  | { action: "checkout"; plan: string; interval: "month" | "year"; extra_seats?: number; extra_talents?: number; ohne_testphase?: boolean; /** Rabattcode, wird am Server nachgeschlagen und direkt angewendet */ code?: string }
  | { action: "change"; plan: string; interval: "month" | "year"; /** nur rechnen lassen, nichts ändern (Vorschau mit Zahlen von Stripe) */ preview?: boolean }
  | { action: "addons"; extra_seats: number; extra_talents: number }
  | { action: "cancel_pending" }
  | { action: "retention"; offer: "discount" }
  | { action: "paypal_checkout"; plan: string; interval: "month" | "year"; ohne_testphase?: boolean; rechnung: Record<string, string> }
  | { action: "paypal_confirm"; subscription_id: string }
  /** 0180: Rückkehr aus dem Stripe-Checkout (?checkout=erfolg&session_id=cs_…) übernehmen, falls der Webhook ausbleibt */
  | { action: "checkout_confirm"; session_id: string }
  /** E (K-484): Test mit Zahlungsart: nach dem Test automatisch weiterlaufen (an) oder ohne Kosten enden (aus) */
  | { action: "trial_autorenew"; on: boolean }
  /** 0201: Vorlagenpaket kaufen (einmalige Zahlung über Stripe) */
  | { action: "package_checkout"; package: string }
  /** 0410: im Test mit Stripe-Probeabo die Zahlungsart entfernen; der Test endet sofort, nichts wird abgebucht */
  | { action: "remove_payment_method" };
export interface BillingChangeResult {
  result?: "changed" | "scheduled" | "addons" | "withdrawn" | "discount" | "applied" | "review" | "stale" | "ignored" | "cancelled"
    // 0180 checkout_confirm
    | "already" | "open" | "duplicate" | "awaiting_payment" | "payment_failed" | "unknown_org" | "preview" | "reserved"
    // 0410 remove_payment_method
    | "removed";
  /** Vorschau des Tarifwechsels (preview): heute fälliger Betrag netto für die Resttage, Ende des Zeitraums, Preis danach */
  timing?: "now" | "period_end";
  today_net_cents?: number | null;
  period_end?: string | null;
  days_left?: number | null;
  next_net_cents?: number | null;
  trial?: boolean;
  /** K-501: Upgrade mit anderer Abrechnung; der Tarif gilt sofort, die neue Abrechnung ab diesem Tag (Laufzeitende) */
  interval_switch_at?: string | null;
  interval_switch?: "failed";
  message?: string;
  effective_at?: string;
  /** checkout: Adresse der Stripe-Zahlseite */
  url?: string;
  trial_kept?: boolean;
}
/** Abo ändern bzw. abschließen über die Serverfunktion billing-change (Rechte und Grenzen prüft der Server). */
export async function billingChange(api: Api, orgId: string, body: BillingChangeBody): Promise<BillingChangeResult> {
  if (!api.billing) throw new ApiError(t("In der Demo gibt es keine Zahlung.", "There is no payment in the demo."), { hint: "billing:demo" });
  return api.billing<BillingChangeResult>("billing-change", { org: orgId, ...body });
}
/** 0412: „Ja, einrichten lassen“ im Dialog „Test beenden“: Support-Ticket der Kategorie „einrichtung“, der Test läuft weiter */
export async function requestTrialSetup(api: Api, orgId: string, note?: string | null): Promise<string> {
  return api.rpc<string>("trial_setup_request", { p_org: orgId, p_note: note?.trim() ? note.trim().slice(0, 1000) : null });
}
/** 0412: heute geltende Fassungen von AGB, AVV und Datenschutzhinweisen (auch ohne Anmeldung, für die Registrierung) */
export async function loadCurrentLegalVersions(api: Api): Promise<Partial<Record<"agency_terms" | "dpa" | "privacy", string>>> {
  const r = await api.read<Record<string, string> | string | null>("legal_versions_public");
  return (typeof r === "string" ? JSON.parse(r) : r) ?? {};
}
/** Stripe ist auf dem Server nicht eingerichtet (503): dann gelten Zahlungslink bzw. Anfrage an den Betrieb wie bisher. */
export function stripeNotConfigured(e: unknown): boolean {
  return e instanceof ApiError && (e.hint === "billing:stripe_not_configured" || e.hint === "billing:price_missing" || e.hint === "billing:demo");
}

export interface LegalConsentRow { name: string; email: string; active: boolean; kind: string; version: string; accepted_at: string; current_version: string | null; method?: string | null; customer_org_id?: string | null }
/** Nachweis: wer hat wann welcher Fassung von AGB, AVV und Datenschutz zugestimmt (Admin, 0057). */
export async function loadLegalConsents(api: Api, orgId: string): Promise<LegalConsentRow[]> {
  const r = await api.read<LegalConsentRow[] | null>("org_legal_consents", { p_org: orgId });
  return r ?? [];
}

export async function loadStorageUsage(api: Api, orgId: string): Promise<{ bytes: number; limit_gb: number }> {
  const r = await api.read<{ bytes: number; limit_gb: number } | string>("org_storage_usage", { p_org: orgId });
  return (typeof r === "string" ? JSON.parse(r) : r) as { bytes: number; limit_gb: number };
}

// ── Standardprovision (0042) ────────────────────────────────────────────────────

export async function openDealsOnDefault(api: Api, orgId: string): Promise<number> {
  return Number((await api.read<number | null>("org_open_deals_on_default", { p_org: orgId })) ?? 0);
}
/** Neue Standardprovision; keepRunning: laufende Kooperationen ohne eigenen Satz bleiben beim bisherigen Satz. */
export async function setDefaultCommission(api: Api, orgId: string, bp: number, keepRunning: boolean): Promise<{ pinned: number }> {
  return api.rpc<{ pinned: number }>("org_set_default_commission", { p_org: orgId, p_bp: bp, p_keep_running: keepRunning });
}

// ── Logo (Bucket branding, 0031) ────────────────────────────────────────────────

export const LOGO_TYPES: Record<string, string> = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" };
/** Logo hochladen und als logo_path eintragen: '<agentur>/logo-<zeit>.<endung>' (öffentliche Adresse für Mails). */
export async function uploadLogo(api: Api, orgId: string, file: Blob & { type: string }): Promise<string> {
  const ext = LOGO_TYPES[file.type];
  if (!ext) throw new ApiError(t("Bitte ein Logo als PNG, JPEG oder WebP wählen. SVG zeigen viele Mailprogramme nicht.", "Please choose a logo as PNG, JPEG or WebP. Many email programs do not show SVG."), { code: "P0001" });
  if (file.size > 1024 * 1024) throw new ApiError(t("Das Logo darf höchstens 1 MB groß sein.", "The logo may be at most 1 MB."), { code: "P0001" });
  // Praxistest K-108: Die Typangabe allein genügt nicht (eine Textdatei namens logo.png und eine leere Datei wurden als Logo
  // gespeichert, Rechnungen kamen dann ohne Logo). Geprüft wird der Anfang der Datei.
  const kopf = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const echt = ext === "png" ? [0x89, 0x50, 0x4e, 0x47].every((b, i) => kopf[i] === b)
    : ext === "jpg" ? kopf[0] === 0xff && kopf[1] === 0xd8 && kopf[2] === 0xff
    : [0x52, 0x49, 0x46, 0x46].every((b, i) => kopf[i] === b) && [0x57, 0x45, 0x42, 0x50].every((b, i) => kopf[8 + i] === b);
  if (file.size === 0 || !echt) throw new ApiError(t("Die Datei ist kein gültiges Bild. Bitte exportiere das Logo neu als PNG, JPEG oder WebP.", "The file is not a valid image. Please export the logo again as PNG, JPEG or WebP."), { code: "P0001" });
  const path = `${orgId}/logo-${Date.now()}.${ext}`;
  await api.upload("branding", path, file);
  await updateSettings(api, orgId, { logo_path: path });
  return path;
}
export async function removeLogo(api: Api, orgId: string): Promise<void> {
  await updateSettings(api, orgId, { logo_path: null });
}

// ── Export ──────────────────────────────────────────────────────────────────────

/** Zeilen je Abruf beim Vollexport (org_export_table erlaubt höchstens 2000) */
export const EXPORT_PAGE_SIZE = 500;

/**
 * Vollständiger Export der Agentur (nur Admins; auch in der Nur-Lesen-Phase), seitenweise (0193): zuerst der Kopf
 * (org_export_manifest: Konten, Tabellen mit Zeilenzahl, Dateiliste, Content-Ablage; protokolliert), dann jede Tabelle in
 * Seiten zu EXPORT_PAGE_SIZE Zeilen (org_export_table, fortgesetzt am letzten Schlüssel). So stößt auch ein großes Konto an
 * keine Zeitgrenze; das Ergebnis hat dieselbe Form wie früher org_export_all.
 */
export async function loadFullExport(api: Api, orgId: string,
  onProgress?: (p: { table: string; index: number; total: number; rows: number }) => void): Promise<FullExport> {
  const parse = <T,>(v: T | string): T => (typeof v === "string" ? JSON.parse(v) : v) as T;
  const m = parse(await api.rpc<ExportManifest | string>("org_export_manifest", { p_org: orgId }));
  const tables: Record<string, Record<string, unknown>[]> = {};
  for (const name of m.tables) tables[name] = [];
  const names = m.tables.filter((n) => Number(m.row_counts[n] ?? 0) > 0);
  for (const [i, name] of names.entries()) {
    const rows = tables[name]!;
    let cursor: Record<string, unknown> | null = null;
    do {
      onProgress?.({ table: name, index: i + 1, total: names.length, rows: rows.length });
      const page: ExportTablePage = parse(await api.read<ExportTablePage | string>("org_export_table",
        { p_org: orgId, p_table: name, p_cursor: cursor, p_limit: EXPORT_PAGE_SIZE }));
      rows.push(...page.rows);
      cursor = page.next;
    } while (cursor);
  }
  const { page_limit: _limit, ...head } = m;
  return { ...head, tables };
}

/**
 * Dateien der Content-Ablage für den Vollexport (0193): die Serverfunktion medien liefert sie seitenweise mit Download-Links
 * (7 Tage gültig). Ist der Medienspeicher nicht eingerichtet oder nicht erreichbar, bricht der Export nicht ab: die Liste
 * bleibt leer und error nennt den Grund (steht in LIESMICH; die Zeilen stehen trotzdem in tabellen/media_files.csv).
 */
export async function loadMediaExport(api: Api, orgId: string, onProgress?: (loaded: number, total: number) => void): Promise<MediaExport> {
  const items: MediaExportItem[] = [];
  let after: string | null = null;
  let expiresAt: string | null = null;
  try {
    do {
      const r: { items: MediaExportItem[]; next: string | null; total: number; expires_at: string } =
        await api.medien({ action: "export", org: orgId, after });
      items.push(...r.items);
      expiresAt = r.expires_at ?? expiresAt;
      onProgress?.(items.length, Number(r.total) || items.length);
      after = r.next;
    } while (after);
  } catch (e) {
    if (e instanceof ApiError && e.code === "42501") throw e;
    return { items, expires_at: items.length ? expiresAt : null, error: (e as Error).message || t("Der Medienspeicher hat nicht geantwortet.", "The media storage did not respond.") };
  }
  return { items, expires_at: items.length ? expiresAt : null, error: null };
}

export async function runExport(api: Api, orgId: string, kind: ExportKind, range: { from: string; to: string }): Promise<ExportInvoiceRow[] | ExportFlatRow[]> {
  const fn = kind === "invoices" ? "export_invoices" : "export_flat_taxed";
  const rows = await api.read<ExportInvoiceRow[] | ExportFlatRow[] | null>(fn, { p_org: orgId, p_from: range.from, p_to: range.to });
  return rows ?? [];
}

// ── Einrichtung ─────────────────────────────────────────────────────────────────

export interface NewAgencyInput {
  name: string;
  country: string;
}
/**
 * Agentur anlegen und Firmendaten speichern in einem Schritt (organization_setup_create, 0042): scheitert etwas, entsteht
 * keine Agentur. Belegte Kurznamen bekommen in der Datenbank eine Nummer.
 */
export async function createAgencyWithData(api: Api, input: NewAgencyInput & { settings: SettingsPatch; legal: LegalPatch }): Promise<string> {
  return api.rpc<string>("organization_setup_create", {
    p_name: input.name.trim(), p_slug: slugify(input.name), p_country: countryDefaults(input.country).code, p_settings: input.settings, p_legal: input.legal,
  });
}

/**
 * Creator-Konto anlegen (create_creator_account, 0178): Konto der Art „creator“ im Tarif Creator, die Person wird Inhaber und
 * zugleich ihr einziger Creator; Rechnungsweg über das eigene Konto, keine Provision.
 */
export interface NewCreatorAccountInput {
  name: string;
  country: string;
  segment: string | null;
  firstName: string;
  lastName: string;
  smallBusiness: boolean;
  /** 0518 (B6): bestätigt „mindestens 18 Jahre“; ohne lehnt die Datenbank ab */
  adult: boolean;
}
export async function createCreatorAccount(api: Api, input: NewCreatorAccountInput): Promise<string> {
  return api.rpc<string>("create_creator_account", {
    p_name: input.name.trim(), p_country: countryDefaults(input.country).code, p_segment: input.segment,
    p_profile: { first_name: input.firstName.trim(), last_name: input.lastName.trim(), adult: input.adult },
    p_settings: { small_business: input.smallBusiness },
    p_legal: {},
  });
}

/**
 * Agentur anlegen (create_organization): der Aufrufer wird Admin, Testphase im kleinsten Tarif, Rechtsdatensatz,
 * Mahnleiter-Kopf und Muster-Vertragsvorlagen entstehen mit. Belegte Kurznamen bekommen eine Nummer.
 */
export async function createAgency(api: Api, input: NewAgencyInput): Promise<string> {
  const d = countryDefaults(input.country);
  const base = slugify(input.name);
  let last: unknown = null;
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      return await api.rpc<string>("create_organization", {
        p_name: input.name.trim(), p_slug: slugCandidate(base, attempt), p_country: d.code, p_timezone: d.timezone, p_locale: d.locale, p_currency: d.currency,
      });
    } catch (e) {
      last = e;
      if (!(e instanceof ApiError) || e.code !== "23505") throw e;
    }
  }
  throw last instanceof Error ? last : new ApiError(t("Die Agentur konnte nicht angelegt werden.", "The agency could not be created."));
}

export interface EinrichtungData extends FirmaData {
  defaults: Record<string, unknown>;
  policy: PolicyRow | null;
  steps: StepDbRow[];
  rates: BaseRateRow[];
  templates: TemplateRow[];
  platformTexts: PlatformTextRow[];
  orgTexts: OrgTextRow[];
  talents: TalentMiniRow[];
  setup: SetupStatus;
}
export async function loadEinrichtung(api: Api, orgId: string): Promise<EinrichtungData> {
  const [firma, defaults, policy, steps, rates, templates, platformTexts, orgTexts, talents, setup] = await Promise.all([
    loadFirma(api, orgId),
    loadDefaults(api),
    api.select<PolicyRow>("dunning_policies", { columns: "org_id, interest_enabled, surcharge_bp, fee_mode, flat_fee_cents, fee_currency, creator_claims_costs, interest_rate_mode, contract_rate_bp, contract_basis, interest_start, auto_send, auto_send_scope, auto_send_creator, updated_at", filters: byOrg(orgId) }),
    api.select<StepDbRow>("dunning_steps", { filters: byOrg(orgId), order: [["step", "asc"]] }),
    api.select<BaseRateRow>("dunning_base_rates", { filters: byOrg(orgId), order: [["valid_from", "desc"]] }),
    api.select<TemplateRow>("contract_templates", { columns: "id, name, language, status, version, kind_id, description, updated_at", filters: byOrg(orgId), order: [["name", "asc"]] }),
    api.select<PlatformTextRow>("platform_texts", { order: [["key", "asc"], ["language", "asc"]] }),
    api.select<OrgTextRow>("org_texts", { filters: byOrg(orgId) }),
    api.select<TalentMiniRow>("talents", { columns: "id, first_name, last_name, display_name, email, status", filters: byOrg(orgId), order: [["first_name", "asc"]] }),
    loadSetupStatus(api, orgId),
  ]);
  return { ...firma, defaults, policy: policy[0] ?? null, steps, rates, templates, platformTexts, orgTexts, talents, setup };
}

/** Creator anlegen und einladen: Datensatz mit Status „eingeladen“, danach Einladung mit Mail-Auftrag (0025). */
export async function inviteCreator(api: Api, orgId: string, input: { first_name: string; last_name: string | null; email: string }) {
  const rows = await api.insert<TalentMiniRow>("talents", {
    org_id: orgId, first_name: input.first_name, last_name: input.last_name, email: input.email, status: "invited",
    display_name: [input.first_name, input.last_name].filter(Boolean).join(" "),
  });
  const talent = need(rows, t("Der Creator", "The creator"))[0]!;
  const inv = await api.rpc<{ invitation_id: string; expires_at: string }>("invitation_create", { p_kind: "talent", p_email: input.email, p_talent: talent.id });
  return { talent, invitation: inv };
}
